import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { User, Shield, Bell, Github, Save, X } from 'lucide-react';
import securityApi from '../api/securityApi';

const SettingsPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [githubAuth, setGithubAuth] = useState({ authenticated: false });

  // Profile form state
  const [editedName, setEditedName] = useState('');
  const [hasProfileChanges, setHasProfileChanges] = useState(false);

  // Scanner configuration state
  const [severityThreshold, setSeverityThreshold] = useState('medium');
  const [hasScannerChanges, setHasScannerChanges] = useState(false);

  // Notification preferences state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [hasNotificationChanges, setHasNotificationChanges] = useState(false);

  useEffect(() => {
    fetchUserData();
    checkGithubConnection();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const userData = await securityApi.getCurrentUser();
      setUser(userData);
      setEditedName(userData?.name || '');
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setLoading(false);
    }
  };

  const checkGithubConnection = async () => {
    try {
      const authStatus = await securityApi.checkGithubAuth();
      setGithubAuth(authStatus);
    } catch (error) {
      console.error('Error checking GitHub auth:', error);
      setGithubAuth({ authenticated: false });
    }
  };

  const handleProfileSave = async () => {
    setSaving(true);
    // Simulate API call to save profile changes
    setTimeout(() => {
      setUser({ ...user, name: editedName });
      setHasProfileChanges(false);
      setSaving(false);
    }, 1000);
  };

  const handleProfileCancel = () => {
    setEditedName(user?.name || '');
    setHasProfileChanges(false);
  };

  const handleScannerSave = async () => {
    setSaving(true);
    // Simulate API call to save scanner configuration
    setTimeout(() => {
      setHasScannerChanges(false);
      setSaving(false);
    }, 1000);
  };

  const handleScannerCancel = () => {
    setSeverityThreshold('medium');
    setHasScannerChanges(false);
  };

  const handleNotificationSave = async () => {
    setSaving(true);
    // Simulate API call to save notification preferences
    setTimeout(() => {
      setHasNotificationChanges(false);
      setSaving(false);
    }, 1000);
  };

  const handleNotificationCancel = () => {
    setEmailNotifications(true);
    setHasNotificationChanges(false);
  };

  const handleGithubReconnect = () => {
    securityApi.initiateGithubAuth(true);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'scanner', label: 'Scanner Configuration', icon: Shield },
    { id: 'integrations', label: 'Integrations', icon: Github },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8 flex items-center justify-center">
        <div className="text-gray-700 text-lg">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Settings</h1>
          <p className="text-gray-600">Manage your account and preferences</p>
        </div>

        {/* Settings Container */}
        <div className="bg-white border border-gray-100 shadow-sm rounded-lg overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-100 bg-gray-50">
            {/* Horizontally scrollable on narrow screens; the tab labels are too
                long to wrap or truncate without losing meaning. */}
            <div className="flex overflow-x-auto whitespace-nowrap -mb-px">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex flex-shrink-0 items-center gap-2 px-4 sm:px-6 py-4 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'text-gray-900 bg-white border-b-2 border-teal-600'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Profile Information</h2>

                  <div className="space-y-4">
                    {/* Email (read-only) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={user?.email || ''}
                        disabled
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                      />
                      <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
                    </div>

                    {/* Name (editable) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Display Name
                      </label>
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => {
                          setEditedName(e.target.value);
                          setHasProfileChanges(e.target.value !== user?.name);
                        }}
                        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="Enter your name"
                      />
                    </div>

                    {/* Avatar */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Profile Picture
                      </label>
                      <div className="flex items-center gap-4">
                        {user?.picture ? (
                          <img
                            src={user.picture}
                            alt="Profile"
                            className="w-16 h-16 rounded-full border-2 border-gray-200"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-2xl font-bold">
                            {editedName ? editedName.charAt(0).toUpperCase() : 'U'}
                          </div>
                        )}
                        <p className="text-sm text-gray-500">Profile picture is synced from your authentication provider</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save/Cancel Buttons */}
                {hasProfileChanges && (
                  <div className="flex gap-3 pt-4 border-t border-gray-100">
                    <button
                      onClick={handleProfileSave}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleProfileCancel}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Scanner Configuration Tab */}
            {activeTab === 'scanner' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Scanner Settings</h2>

                  <div className="space-y-4">
                    {/* Severity Threshold */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Minimum Severity Threshold
                      </label>
                      <select
                        value={severityThreshold}
                        onChange={(e) => {
                          setSeverityThreshold(e.target.value);
                          setHasScannerChanges(true);
                        }}
                        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Only show vulnerabilities at or above this severity level
                      </p>
                    </div>

                    {/* Placeholder settings */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <p className="text-sm text-gray-500">
                        Additional scanner configuration options will be available here in future updates.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Save/Cancel Buttons */}
                {hasScannerChanges && (
                  <div className="flex gap-3 pt-4 border-t border-gray-100">
                    <button
                      onClick={handleScannerSave}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleScannerCancel}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Integrations Tab */}
            {activeTab === 'integrations' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Connected Services</h2>

                  {/* GitHub Integration */}
                  <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-100">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 flex-shrink-0 bg-gray-900 rounded-lg flex items-center justify-center">
                          <Github className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">GitHub</h3>
                          <p className="text-sm text-gray-600 mb-3">
                            Connect your GitHub account to scan private repositories
                          </p>
                          <div className="flex flex-wrap items-center gap-2">
                            <div
                              className={`w-2 h-2 rounded-full ${
                                githubAuth.authenticated ? 'bg-green-500' : 'bg-red-500'
                              }`}
                            />
                            <span className="text-sm text-gray-600">
                              {githubAuth.authenticated ? 'Connected' : 'Not Connected'}
                            </span>
                            {githubAuth.authenticated && githubAuth.github_user && (
                              <span className="text-sm text-gray-500">
                                as {githubAuth.github_user}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleGithubReconnect}
                        className="w-full sm:w-auto flex-shrink-0 px-4 py-2 min-h-[44px] bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors text-sm"
                      >
                        {githubAuth.authenticated ? 'Reconnect' : 'Connect'}
                      </button>
                    </div>
                  </div>

                  {/* Placeholder for future integrations */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 mt-4">
                    <p className="text-sm text-gray-500">
                      More integrations coming soon (Slack, Jira, PagerDuty, etc.)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Notification Preferences</h2>

                  <div className="space-y-4">
                    {/* Email Notifications Toggle */}
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <div>
                        <h3 className="text-gray-900 font-medium mb-1">Email Notifications</h3>
                        <p className="text-sm text-gray-500">
                          Receive email alerts for new security findings
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setEmailNotifications(!emailNotifications);
                          setHasNotificationChanges(true);
                        }}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          emailNotifications ? 'bg-teal-600' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            emailNotifications ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>

                    {/* Placeholder for additional notification settings */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <p className="text-sm text-gray-500">
                        Additional notification preferences (Slack, webhooks, etc.) will be available here in future updates.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Save/Cancel Buttons */}
                {hasNotificationChanges && (
                  <div className="flex gap-3 pt-4 border-t border-gray-100">
                    <button
                      onClick={handleNotificationSave}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleNotificationCancel}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
